import React from 'react';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { AppRouter } from './router';
import './index.css';

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>Please refresh the page or try again later.</p>
          {import.meta.env.DEV && (
            <pre style={{ textAlign: 'left', color: 'red' }}>
              {this.state.error?.toString()}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

// Initialize the app
const initApp = () => {
  const container = document.getElementById('root');
  
  if (!container) {
    console.error('Failed to find the root element');
    return;
  }

  const root = createRoot(container);
  
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <HelmetProvider>
          <AppRouter />
        </HelmetProvider>
      </ErrorBoundary>
    </React.StrictMode>
  );
};

// Add preconnect for external domains
const addPreconnectLinks = () => {
  const preconnectLinks = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
    'https://www.google-analytics.com',
    'https://www.googletagmanager.com'
  ];

  preconnectLinks.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Initialize the application
const initializeApp = () => {
  try {
    // Add preconnect links
    addPreconnectLinks();
    
    // Initialize the React app
    initApp();
    
    if (import.meta.env.PROD) {
      // Production-only configurations
      console.log('Running in production mode');
      
      // Global error handlers
      window.addEventListener('error', (event) => {
        console.error('Uncaught error:', event.error);
      });

      window.addEventListener('unhandledrejection', (event) => {
        console.error('Unhandled promise rejection:', event.reason);
      });
    } else {
      console.log('Running in development mode');
    }
    
    console.log('Application initialized successfully');
  } catch (error) {
    console.error('Failed to initialize application:', error);
  }
};

// Start the application
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeApp);
} else {
  initializeApp();
}
